import { useTranslations } from '@vocab/react';
import { Text, Box, Strong, Hidden } from 'braid-design-system';
import { vars } from 'braid-design-system/css';
import type * as React from 'react';

import type { BUCKETS } from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from '../ActionToolbar/.vocab';

interface Props {
  matchingApplicationsCount: number;
  bucket: BUCKETS;
  hasSearchText: boolean;
  selectedFilterCount: number;
}

export const SearchResultSummary: React.FC<Props> = ({
  bucket,
  matchingApplicationsCount,
  hasSearchText,
  selectedFilterCount,
}) => {
  const { t } = useTranslations(translations);

  const { getLabelFromBucket } = useBucketLabel();

  const statusFolder = getLabelFromBucket(bucket).toLocaleLowerCase();

  if (!hasSearchText && selectedFilterCount <= 0) {
    return (
      <Hidden above="mobile">
        <Box paddingY="xxsmall" />
      </Hidden>
    );
  }

  const getResultMessage = () => {
    if (!hasSearchText) {
      return t(
        '<Strong>matchingApplicationsCount applications</Strong> in <Strong>statusFolder</Strong> match your <Strong>filters</Strong>',
        {
          matchingApplicationsCount,
          statusFolder,
          Strong: (children) => <Strong>{children}</Strong>,
        },
      );
    }

    if (selectedFilterCount === 1 && hasSearchText) {
      return t(
        '<Strong>matchingApplicationsCount applications</Strong> match your <Strong>search</Strong>',
        {
          matchingApplicationsCount,
          Strong: (children) => <Strong>{children}</Strong>,
        },
      );
    }

    return t(
      '<Strong>matchingApplicationsCount applications</Strong> match your <Strong>search</Strong> and <Strong>filters</Strong>',
      {
        matchingApplicationsCount,
        Strong: (children) => <Strong>{children}</Strong>,
      },
    );
  };

  return (
    <Box
      padding="medium"
      background="surface"
      style={{
        borderBottom: `1px solid ${vars.borderColor.neutralLight}`,
        borderTopLeftRadius: vars.borderRadius.standard,
        borderTopRightRadius: vars.borderRadius.standard,
      }}
    >
      <Text>{getResultMessage()}</Text>
    </Box>
  );
};
